/**
 * Collection Infinite Scroll with Scroll Position Restoration
 * Handles infinite scrolling and restores scroll position when returning from product pages
 */
(function() {
  'use strict';
  
  // Wait for DOM to be ready before initializing
  function waitForProductGrid() {
    return new Promise((resolve, reject) => {
      // Check if product grid exists
      const productGrid = document.querySelector('#product-grid[data-infinite-scroll]');
      if (productGrid) {
        resolve(productGrid);
        return;
      }
      
      // Wait for it to appear (for when script loads before section)
      const observer = new MutationObserver((mutations, obs) => {
        const grid = document.querySelector('#product-grid[data-infinite-scroll]');
        if (grid) {
          obs.disconnect();
          resolve(grid);
        }
      });
      
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
      
      // Timeout after 5 seconds
      setTimeout(() => {
        observer.disconnect();
        reject(new Error('Product grid not found'));
      }, 5000);
    });
  }
  
  // Initialize when product grid is available
  waitForProductGrid().then((productGrid) => {
    console.log('[Infinite Scroll] Product grid found, initializing...', productGrid);
    initInfiniteScroll(productGrid);
  }).catch((error) => {
    console.warn('[Infinite Scroll] Product grid not found:', error);
    // Product grid not found, script won't run (not a collection page or section not loaded)
  });
  
  function initInfiniteScroll(productGrid) {
    const collectionUrl = productGrid.dataset.collectionUrl;
    const collectionHandle = productGrid.dataset.collectionHandle;
    const sectionId = productGrid.dataset.sectionId;
    const productsPerPage = parseInt(productGrid.dataset.productsPerPage, 10) || 16;
    
    console.log('[Infinite Scroll] Config:', {
      collectionUrl,
      collectionHandle,
      sectionId,
      productsPerPage
    });
    
    // Storage keys
    const SCROLL_POSITION_KEY = `collection_scroll_${collectionHandle}`;
    const CLICKED_PRODUCT_KEY = `collection_clicked_product_${collectionHandle}`;
    const LOADED_PAGES_KEY = `collection_loaded_pages_${collectionHandle}`;
    
    let currentPage = 1;
    let isLoading = false;
    let hasMorePages = true;
    let scrollRestored = false;
    
    // Get stored data
    function getStoredData() {
      try {
        const scrollPos = sessionStorage.getItem(SCROLL_POSITION_KEY);
        const clickedProduct = sessionStorage.getItem(CLICKED_PRODUCT_KEY);
        const clickedProductHandle = sessionStorage.getItem(`${CLICKED_PRODUCT_KEY}_handle`);
        const clickedProductIndex = sessionStorage.getItem(`${CLICKED_PRODUCT_KEY}_index`);
        const loadedPages = sessionStorage.getItem(LOADED_PAGES_KEY);
        
        return {
          scrollPosition: scrollPos ? parseInt(scrollPos, 10) : null,
          clickedProductId: clickedProduct || null,
          clickedProductHandle: clickedProductHandle || null,
          clickedProductIndex: clickedProductIndex ? parseInt(clickedProductIndex, 10) : null,
          loadedPages: loadedPages ? parseInt(loadedPages, 10) : 1
        };
      } catch (e) {
        return { scrollPosition: null, clickedProductId: null, clickedProductHandle: null, clickedProductIndex: null, loadedPages: 1 };
      }
    }
    
    // Save scroll position
    function saveScrollPosition() {
      try {
        const scrollY = window.scrollY || window.pageYOffset;
        sessionStorage.setItem(SCROLL_POSITION_KEY, scrollY.toString());
      } catch (e) {
        console.warn('Could not save scroll position:', e);
      }
    }
    
    // Save clicked product
    function saveClickedProduct(productId) {
      try {
        sessionStorage.setItem(CLICKED_PRODUCT_KEY, productId);
      } catch (e) {
        console.warn('Could not save clicked product:', e);
      }
    }
    
    // Save loaded pages count
    function saveLoadedPages(page) {
      try {
        sessionStorage.setItem(LOADED_PAGES_KEY, page.toString());
      } catch (e) {
        console.warn('Could not save loaded pages:', e);
      }
    }
    
    // Clear stored data (when filters change)
    function clearStoredData() {
      try {
        sessionStorage.removeItem(SCROLL_POSITION_KEY);
        sessionStorage.removeItem(CLICKED_PRODUCT_KEY);
        sessionStorage.removeItem(`${CLICKED_PRODUCT_KEY}_handle`);
        sessionStorage.removeItem(`${CLICKED_PRODUCT_KEY}_index`);
        sessionStorage.removeItem(LOADED_PAGES_KEY);
      } catch (e) {
        console.warn('Could not clear stored data:', e);
      }
    }
    
    // Track product clicks using event delegation (more efficient)
    function trackProductClicks() {
      // Use event delegation on the product grid
      const productGrid = document.querySelector('#product-grid');
      if (!productGrid) return;
      
      // Remove old listener if exists
      if (productGrid._clickHandler) {
        productGrid.removeEventListener('click', productGrid._clickHandler);
      }
      
      // Create new handler
      productGrid._clickHandler = function(e) {
        const link = e.target.closest('a[href*="/products/"]');
        if (link) {
          // Try to find product item - check for li with id or data-product-id
          const productItem = link.closest('li[id], li[data-product-id]');
          if (productItem) {
            // Get product ID - prefer element id (what's actually rendered), fallback to data-product-id
            const productId = productItem.id || productItem.getAttribute('data-product-id');
            const productHandle = productItem.getAttribute('data-product-handle');
            
            // Extract handle from URL if not in attribute
            let handle = productHandle;
            if (!handle && link.href) {
              const match = link.href.match(/\/products\/([^\/\?]+)/);
              if (match) handle = match[1];
            }
            
            // Save both ID and handle for better matching
            if (productId) {
              saveClickedProduct(productId);
            }
            if (handle) {
              try {
                sessionStorage.setItem(`${CLICKED_PRODUCT_KEY}_handle`, handle);
              } catch (e) {}
            }
            saveScrollPosition();
            
            // Also save the product index for better restoration
            const allProducts = Array.from(productGrid.querySelectorAll('li[id], li[data-product-id]'));
            const productIndex = allProducts.indexOf(productItem);
            try {
              sessionStorage.setItem(`${CLICKED_PRODUCT_KEY}_index`, productIndex.toString());
            } catch (e) {}
          }
        }
      };
      
      productGrid.addEventListener('click', productGrid._clickHandler, true);
    }
    
    // Load more products - optimized version with faster loading
    async function loadMoreProducts(forcePage = null) {
      if (isLoading || !hasMorePages) {
        return false;
      }
    
      isLoading = true;
      const loader = document.getElementById('infinite-scroll-loader');
      if (loader) loader.classList.remove('hidden');
      
      const pageToLoad = forcePage || (currentPage + 1);
      currentPage = pageToLoad;
      
      console.log('[Infinite Scroll] Loading page', pageToLoad);
      
      try {
      // Build URL with current filters and sort
      const url = new URL(collectionUrl, window.location.origin);
      url.searchParams.set('page', pageToLoad);
      
      // Preserve existing query parameters (filters, sort, etc.)
      const currentParams = new URLSearchParams(window.location.search);
      currentParams.forEach((value, key) => {
        if (key !== 'page') {
          url.searchParams.set(key, value);
        }
      });
      
      // Start fetch immediately without waiting
      const fetchPromise = fetch(url.toString(), {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        },
        cache: 'default', // Use cache for faster loading
        priority: 'high' // Prioritize this request
      });
      
      const response = await fetchPromise;
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Find product grid in the response - try multiple selectors
      let newProductGrid = doc.querySelector(`#product-grid[data-id="${sectionId}"]`);
      if (!newProductGrid) {
        newProductGrid = doc.querySelector('#product-grid');
      }
      
      // Also try finding the section content directly
      if (!newProductGrid) {
        const sectionContent = doc.querySelector(`[data-section-id="${sectionId}"]`);
        if (sectionContent) {
          newProductGrid = sectionContent.querySelector('#product-grid');
        }
      }
      
      if (newProductGrid) {
        const newProducts = newProductGrid.querySelectorAll('li.grid__item, li[id], li[data-product-id]');
        
        // Check if there are no products or if we've reached the end
        if (newProducts.length === 0) {
          hasMorePages = false;
          if (loader) loader.classList.add('hidden');
          return false;
        }
        
        // Check if pagination exists and has a next page link
        const responsePagination = doc.querySelector('#AjaxinatePagination, #Huratips-Pagination, [id*="Pagination"]');
        if (responsePagination) {
          const nextLink = responsePagination.querySelector('a[href*="page="]');
          if (!nextLink || nextLink.textContent.toLowerCase().includes('loading')) {
            // No next page link means we're on the last page
            hasMorePages = false;
          }
        }
        
        const currentGrid = document.querySelector(`#product-grid[data-id="${sectionId}"]`) || document.querySelector('#product-grid');
        
        if (!currentGrid) {
          hasMorePages = false;
          if (loader) loader.classList.add('hidden');
          return false;
        }
        
        // Use DocumentFragment for better performance
        const fragment = document.createDocumentFragment();
        
        // Process items in batches for better performance
        const processItem = (item, index) => {
          // Clone the item to avoid DOM issues
          const clonedItem = item.cloneNode(true);
          
          // Enable lazy loading for new items (only first few load immediately)
          const images = clonedItem.querySelectorAll('img');
          images.forEach(img => {
            // Handle lazy loading attributes
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
            }
            if (img.hasAttribute('data-srcset')) {
              img.srcset = img.getAttribute('data-srcset');
              img.removeAttribute('data-srcset');
            }
            // Only lazy load images beyond the first 4 items
            if (index >= 4 && !img.hasAttribute('loading')) {
              img.setAttribute('loading', 'lazy');
            }
          });
          
          // Update lazy load attributes for background images
          const elementsWithBg = clonedItem.querySelectorAll('[data-bg]');
          elementsWithBg.forEach(el => {
            if (el.dataset.bg) {
              el.style.backgroundImage = `url(${el.dataset.bg})`;
              el.removeAttribute('data-bg');
            }
          });
          
          fragment.appendChild(clonedItem);
        };
        
        // Process all items
        newProducts.forEach(processItem);
        
        // Append all at once for better performance
        currentGrid.appendChild(fragment);
        
        // Use requestIdleCallback if available for non-critical updates
        if (window.requestIdleCallback) {
          requestIdleCallback(() => {
            // Trigger any lazy-loaded images
            const lazyImages = currentGrid.querySelectorAll('img[loading="lazy"]');
            if ('IntersectionObserver' in window) {
              const imageObserver = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                  if (entry.isIntersecting) {
                    const img = entry.target;
                    if (img.dataset.src) {
                      img.src = img.dataset.src;
                    }
                    imageObserver.unobserve(img);
                  }
                });
              });
              lazyImages.forEach(img => imageObserver.observe(img));
            }
          });
        }
        
        // Re-initialize any product card functionality
        if (window.Shopify && window.Shopify.designMode) {
          document.dispatchEvent(new CustomEvent('shopify:section:load'));
        }
        
        // Track clicks on new products (event delegation handles this automatically)
        
        saveLoadedPages(currentPage);
        
        if (loader) loader.classList.add('hidden');
        
        // Re-check if there are more pages after loading
        const currentPagination = document.querySelector('#AjaxinatePagination, #Huratips-Pagination, [id*="Pagination"]');
        if (currentPagination) {
          const nextLink = currentPagination.querySelector('a[href*="page="]');
          if (!nextLink || nextLink.textContent.toLowerCase().includes('loading')) {
            hasMorePages = false;
          }
        }
        
        // Preload next page in background for even faster loading
        if (hasMorePages) {
          // Small delay to not overwhelm, then preload
          setTimeout(() => {
            if (hasMorePages) { // Double check after delay
              const nextPageUrl = new URL(collectionUrl, window.location.origin);
              nextPageUrl.searchParams.set('page', currentPage + 1);
              const currentParams = new URLSearchParams(window.location.search);
              currentParams.forEach((value, key) => {
                if (key !== 'page') {
                  nextPageUrl.searchParams.set(key, value);
                }
              });
              // Prefetch next page
              const link = document.createElement('link');
              link.rel = 'prefetch';
              link.href = nextPageUrl.toString();
              document.head.appendChild(link);
            }
          }, 300);
        }
        
        return true;
      } else {
        hasMorePages = false;
        if (loader) loader.classList.add('hidden');
        return false;
      }
      } catch (error) {
        console.error('[Infinite Scroll] Error loading more products:', error);
        hasMorePages = false;
        if (loader) loader.classList.add('hidden');
        return false;
      } finally {
        isLoading = false;
      }
    }
    
    // Load all pages up to a specific page - optimized parallel loading
    async function loadPagesUpTo(targetPage) {
      if (targetPage <= currentPage) return;
    
      const pagesToLoad = [];
      for (let i = currentPage + 1; i <= targetPage; i++) {
        pagesToLoad.push(i);
      }
      
      // Load pages in parallel batches of 2 for better performance
      const batchSize = 2;
      for (let i = 0; i < pagesToLoad.length; i += batchSize) {
        const batch = pagesToLoad.slice(i, i + batchSize);
        await Promise.all(batch.map(page => loadMoreProducts(page)));
        
        // Small delay between batches
        if (i + batchSize < pagesToLoad.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
    }
    
    // Wait for DOM to be ready
    function waitForDOM() {
      return new Promise((resolve) => {
        if (document.readyState === 'complete') {
          resolve();
          return;
        }
        
        const checkReady = () => {
          if (document.readyState === 'complete') {
            resolve();
          } else {
            setTimeout(checkReady, 50);
          }
        };
        checkReady();
      });
    }
    
    // Wait for product grid to be visible
    function waitForProductGridInner() {
      return new Promise((resolve) => {
      const productGrid = document.querySelector('#product-grid');
      if (productGrid && productGrid.children.length > 0) {
        resolve();
        return;
      }
      
      // Use MutationObserver to wait for grid to populate
      const observer = new MutationObserver((mutations, obs) => {
        const grid = document.querySelector('#product-grid');
        if (grid && grid.children.length > 0) {
          obs.disconnect();
          resolve();
        }
      });
      
      const container = document.querySelector('#ProductGridContainer') || document.body;
      observer.observe(container, {
        childList: true,
        subtree: true
      });
      
        // Timeout after 3 seconds
        setTimeout(() => {
          observer.disconnect();
          resolve();
        }, 3000);
      });
    }
    
    // Wait for critical images to load
    function waitForImages() {
      return new Promise((resolve) => {
      const images = document.querySelectorAll('#product-grid img');
      if (images.length === 0) {
        resolve();
        return;
      }
      
      let loaded = 0;
      const total = Math.min(images.length, 12); // Wait for first 12 images
      
      const checkComplete = () => {
        loaded++;
        if (loaded >= total) {
          resolve();
        }
      };
      
      images.forEach((img, index) => {
        if (index >= total) return;
        
        if (img.complete) {
          checkComplete();
        } else {
          img.addEventListener('load', checkComplete, { once: true });
          img.addEventListener('error', checkComplete, { once: true });
        }
      });
      
        // Timeout after 2 seconds
        setTimeout(resolve, 2000);
      });
    }
    
    // Restore scroll position - improved version with better waiting
    async function restoreScrollPosition(retryCount = 0) {
      if (scrollRestored && retryCount === 0) return;
    
    const maxRetries = 3;
    if (retryCount > maxRetries) {
      scrollRestored = true;
      return;
    }
    
    const stored = getStoredData();
    
    // Wait for DOM to be fully ready
    await waitForDOM();
    
    // Wait for product grid to be populated
    await waitForProductGridInner();
    
    // Calculate initial page based on loaded products
    const initialProducts = document.querySelectorAll('#product-grid li[id], #product-grid li[data-product-id]').length;
    const initialPage = Math.ceil(initialProducts / productsPerPage);
    currentPage = Math.max(initialPage, 1);
    
    // If we have a stored page number, load all pages up to that point
    if (stored.loadedPages > currentPage) {
      await loadPagesUpTo(stored.loadedPages);
      // Wait again after loading pages
      await waitForProductGridInner();
    }
    
    // Wait for critical images to load
    await waitForImages();
    
    // Additional small delay for layout
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Try multiple methods to find the clicked product
    let productItem = null;
    
    if (stored.clickedProductId) {
      // Method 1: Find by element ID (primary - what's actually rendered)
      productItem = document.getElementById(stored.clickedProductId);
      
      // Method 2: Find by data-product-id attribute (fallback)
      if (!productItem) {
        productItem = document.querySelector(`li[data-product-id="${stored.clickedProductId}"]`);
      }
      
      // Method 3: Find by product handle
      if (!productItem && stored.clickedProductHandle) {
        productItem = document.querySelector(`li[data-product-handle="${stored.clickedProductHandle}"]`);
      }
      
      // Method 4: Find by index (fallback)
      if (!productItem && stored.clickedProductIndex !== null) {
        const allProducts = Array.from(document.querySelectorAll('#product-grid li[id], #product-grid li[data-product-id]'));
        if (allProducts[stored.clickedProductIndex]) {
          productItem = allProducts[stored.clickedProductIndex];
        }
      }
    }
    
    if (productItem) {
      // productItem.classList.add('product-item-highlight');
      
      // Wait for layout to settle
      await new Promise(resolve => requestAnimationFrame(resolve));
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Scroll to product
      productItem.scrollIntoView({ behavior: 'auto', block: 'center' });
      
      // Also restore exact scroll position for precision
      if (stored.scrollPosition) {
        await new Promise(resolve => setTimeout(resolve, 100));
        requestAnimationFrame(() => {
          window.scrollTo({
            top: stored.scrollPosition,
            behavior: 'auto'
          });
        });
      }
      
      // Remove highlight after 3 seconds
      // setTimeout(() => {
      //   if (productItem) {
      //     productItem.classList.remove('product-item-highlight');
      //   }
      // }, 3000);
      
      scrollRestored = true;
    } else if (stored.scrollPosition) {
      // If product not found but scroll position exists, restore it
      await new Promise(resolve => requestAnimationFrame(resolve));
      window.scrollTo({
        top: stored.scrollPosition,
        behavior: 'auto'
      });
      scrollRestored = true;
    } else if (retryCount < maxRetries) {
      // Retry if we have data but couldn't find the product
      setTimeout(() => {
        restoreScrollPosition(retryCount + 1);
      }, 300);
      } else {
        scrollRestored = true; // Mark as restored even if no data
      }
    }
    
    // Infinite scroll handler - optimized for faster loading
    function handleInfiniteScroll() {
      if (isLoading || !hasMorePages) return;
      
      const scrollTop = window.scrollY || window.pageYOffset;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Calculate distance from bottom
      const distanceFromBottom = documentHeight - (scrollTop + windowHeight);
      
      // Load more when user is 800px from bottom (more aggressive)
      // On mobile, use 600px for better performance
      const triggerDistance = window.innerWidth < 768 ? 600 : 800;
      
      if (distanceFromBottom <= triggerDistance) {
        console.log('[Infinite Scroll] Triggering load - distance from bottom:', distanceFromBottom);
        loadMoreProducts();
      }
    }
    
    // Throttle scroll events - optimized for faster response
    let scrollTimeout;
    let lastScrollTime = 0;
    function throttledScrollHandler() {
      const now = Date.now();
      
      // Use requestAnimationFrame for smoother performance
      if (now - lastScrollTime < 50) {
        return;
      }
      
      lastScrollTime = now;
      
      // Cancel previous timeout
      if (scrollTimeout) {
        cancelAnimationFrame(scrollTimeout);
      }
      
      scrollTimeout = requestAnimationFrame(() => {
        saveScrollPosition();
        handleInfiniteScroll();
        scrollTimeout = null;
      });
    }
    
    // Initialize
    function init() {
      // Disable Ajaxinate if it's running (our custom script handles infinite scroll)
      if (window.endlessScroll && typeof window.endlessScroll.destroy === 'function') {
        try {
          window.endlessScroll.destroy();
          window.endlessScroll = null;
        } catch (e) {
          console.warn('Could not destroy Ajaxinate instance:', e);
        }
      }
      
      // Also remove Ajaxinate event listeners if they exist
      const paginationElement = document.querySelector('#AjaxinatePagination, #Huratips-Pagination');
      if (paginationElement) {
        // Hide the pagination element since we're handling infinite scroll
        paginationElement.style.display = 'none';
      }
      
      // Check if there are more pages by looking for pagination
      const checkHasMorePages = () => {
        const pagination = document.querySelector('#AjaxinatePagination, #Huratips-Pagination, [id*="Pagination"]');
        if (pagination) {
          const nextLink = pagination.querySelector('a[href*="page="]');
          if (!nextLink || nextLink.textContent.toLowerCase().includes('loading')) {
            hasMorePages = false;
          } else {
            hasMorePages = true;
          }
        }
      };
      
      // Calculate initial page based on loaded products
      const initialProducts = document.querySelectorAll('#product-grid li[id], #product-grid li[data-product-id]').length;
      const initialPage = Math.ceil(initialProducts / productsPerPage);
      currentPage = Math.max(initialPage, 1);
      
      // Check if there are more pages
      checkHasMorePages();
      
      const stored = getStoredData();
      // Use stored page if it's higher than initial
      if (stored.loadedPages > currentPage) {
        currentPage = stored.loadedPages;
      }
      
      // Track product clicks using event delegation
      trackProductClicks();
      
      // Save scroll position on scroll
      window.addEventListener('scroll', throttledScrollHandler, { passive: true });
      
      // Save scroll position before page unload
      window.addEventListener('beforeunload', saveScrollPosition);
      
      // Also save on visibility change (when user switches tabs)
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          saveScrollPosition();
        }
      });
      
      // Clear stored data when filters/sort change
      const filterForms = document.querySelectorAll('form[id*="Facet"], form[id*="Sort"]');
      filterForms.forEach(form => {
        form.addEventListener('submit', () => {
          clearStoredData();
          currentPage = 1;
        });
      });
      
      // Also clear on URL change (filter updates)
      let lastUrl = window.location.href;
      const urlCheckInterval = setInterval(() => {
        if (window.location.href !== lastUrl) {
          lastUrl = window.location.href;
          // Check if it's a filter change (has filter params)
          if (window.location.search.includes('filter') || window.location.search.includes('sort_by')) {
            clearStoredData();
            currentPage = 1;
            hasMorePages = true;
          }
        }
      }, 200);
      
      // Restore scroll position after page load
      function initScrollRestore() {
        // Reset restoration flag
        scrollRestored = false;
        currentPage = 1;
        
        // Wait for page to be fully interactive
        if (document.readyState === 'complete') {
          restoreScrollPosition();
        } else {
          window.addEventListener('load', () => {
            restoreScrollPosition();
          }, { once: true });
        }
      }
      
      // Handle browser back/forward navigation (bfcache)
      window.addEventListener('pageshow', function(event) {
        // If page was loaded from cache (back/forward), restore scroll
        if (event.persisted) {
          scrollRestored = false;
          currentPage = 1;
          
          // Wait a bit for bfcache restoration
          setTimeout(() => {
            restoreScrollPosition();
          }, 50);
        }
      });
      
      // Also handle popstate (browser back/forward)
      window.addEventListener('popstate', function() {
        scrollRestored = false;
        currentPage = 1;
        
        // Wait for navigation to complete
        setTimeout(() => {
          restoreScrollPosition();
        }, 100);
      });
      
      // Handle focus event (when returning to tab)
      window.addEventListener('focus', function() {
        if (!scrollRestored) {
          setTimeout(() => {
            restoreScrollPosition();
          }, 100);
        }
      });
      
      // Initialize scroll restoration
      initScrollRestore();
      
      // Initial infinite scroll check - faster
      setTimeout(() => {
        console.log('[Infinite Scroll] Initialized - hasMorePages:', hasMorePages, 'currentPage:', currentPage);
        handleInfiniteScroll();
      }, 200);
      
      // Also check on window resize (in case viewport changes)
      let resizeTimeout;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          handleInfiniteScroll();
        }, 100);
      });
    }
    
    // Check if we're coming from a product page (back navigation)
    const isBackNavigation = document.referrer && document.referrer.includes('/products/');
    
    // Start initialization when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        // If back navigation, wait a bit longer for everything to settle
        if (isBackNavigation) {
          setTimeout(init, 100);
        } else {
          init();
        }
      });
    } else {
      // If back navigation and DOM already ready, wait a bit
      if (isBackNavigation) {
        setTimeout(init, 100);
      } else {
        init();
      }
    }
    
    // Also listen for when page becomes visible (handles back button)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && !scrollRestored) {
        setTimeout(() => {
          restoreScrollPosition();
        }, 100);
      }
    });
  } // End of initInfiniteScroll function
})();


